<template>
  <a-card title="图片工具">
    <div class="toolbar">
      <div class="upload-controls">
        <Upload v-model:image-url="imageUrl" :max-size="10 * 1024 * 1024" />
        <a-radio-group v-model:value="analysisMode" @change="handleModeChange">
          <a-radio value="auto">自动解析</a-radio>
          <a-radio value="manual">人工选择</a-radio>
        </a-radio-group>
      </div>
      <span class="hint">请上传图片(10m内)。左键拖动框选区域，右键拖动图片；坐标为 0～1 的相对值。</span>
    </div>

    <div class="workspace">
      <div
        ref="canvasRef"
        class="canvas"
        :class="{ 'is-panning': interaction === 'panning' }"
        @contextmenu.prevent
        @mousedown="startInteraction"
        @mousemove="moveInteraction"
        @mouseleave="clearHover"
      >
        <template v-if="imageUrl">
          <img
            ref="imageRef"
            :src="imageUrl"
            crossorigin="anonymous"
            class="canvas-image"
            :style="imageStyle"
            draggable="false"
            @load="fitImage"
          />
          <div v-if="selectionBox" class="selection" :style="selectionBox" />
          <div v-if="hoverPoint && interaction !== 'selecting'" class="coordinate-tip" :style="coordinateTipStyle">
            x: {{ formatCoordinate(hoverPoint.x) }}, y: {{ formatCoordinate(hoverPoint.y) }}
          </div>
        </template>
        <a-empty v-else description="请先上传图片" />
      </div>

      <a-card size="small" title="选区相对坐标" class="coordinate-panel">
        <a-descriptions :column="1" size="small">
          <a-descriptions-item label="X">{{ selectionCoordinates?.x ?? '--' }}</a-descriptions-item>
          <a-descriptions-item label="Y">{{ selectionCoordinates?.y ?? '--' }}</a-descriptions-item>
          <a-descriptions-item label="W">{{ selectionCoordinates?.w ?? '--' }}</a-descriptions-item>
          <a-descriptions-item label="H">{{ selectionCoordinates?.h ?? '--' }}</a-descriptions-item>
          <a-descriptions-item label="边框比">{{ aspectRatio ?? '--' }}</a-descriptions-item>
          <a-descriptions-item label="真实边框比">{{ actualBorderRatio ?? '--' }}</a-descriptions-item>
          <a-descriptions-item label="原图比例">{{ originalRatio ?? '--' }}</a-descriptions-item>
        </a-descriptions>
      </a-card>
    </div>
  </a-card>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import Upload from '@/components/Upload.vue'

type Point = { x: number; y: number }
type AspectRatio = '3:4' | '1:1' | '9:16'

const imageUrl = ref('')
const analysisMode = ref<'auto' | 'manual'>('manual')
const canvasRef = ref<HTMLElement>()
const imageRef = ref<HTMLImageElement>()
const interaction = ref<'idle' | 'selecting' | 'panning'>('idle')
const pan = ref({ x: 0, y: 0 })
const scale = ref(1)
const dragStart = ref<Point | null>(null)
const selectionStart = ref<Point | null>(null)
const selectionEnd = ref<Point | null>(null)
const hoverPoint = ref<Point | null>(null)
const hoverPosition = ref<Point | null>(null)
const selection = ref<{ x: number; y: number; w: number; h: number } | null>(null)
const aspectRatio = ref<string | null>(null)
const actualBorderRatio = ref<string | null>(null)
const originalRatio = ref<string | null>(null)

const imageStyle = computed(() => ({
  transform: `translate(${pan.value.x}px, ${pan.value.y}px) scale(${scale.value})`,
}))

const selectionBox = computed(() => {
  if (!selectionStart.value || !selectionEnd.value || !imageRef.value) return null
  const imageWidth = imageRef.value.naturalWidth * scale.value
  const imageHeight = imageRef.value.naturalHeight * scale.value
  const start = {
    x: pan.value.x + selectionStart.value.x * imageWidth,
    y: pan.value.y + selectionStart.value.y * imageHeight,
  }
  const end = {
    x: pan.value.x + selectionEnd.value.x * imageWidth,
    y: pan.value.y + selectionEnd.value.y * imageHeight,
  }

  return {
    left: `${Math.min(start.x, end.x)}px`,
    top: `${Math.min(start.y, end.y)}px`,
    width: `${Math.abs(end.x - start.x)}px`,
    height: `${Math.abs(end.y - start.y)}px`,
  }
})

const coordinateTipStyle = computed(() => ({
  left: `${(hoverPosition.value?.x ?? 0) + 12}px`,
  top: `${(hoverPosition.value?.y ?? 0) + 12}px`,
}))

const selectionCoordinates = computed(() => {
  if (!selection.value) return null
  const x = truncateCoordinate(selection.value.x)
  const y = truncateCoordinate(selection.value.y)
  const w = Math.min(truncateCoordinate(selection.value.w), maximumDisplaySize(x))
  const h = Math.min(truncateCoordinate(selection.value.h), maximumDisplaySize(y))

  return {
    x: formatCoordinate(x),
    y: formatCoordinate(y),
    w: formatCoordinate(w),
    h: formatCoordinate(h),
  }
})

watch(imageUrl, () => {
  clearSelection()
  hoverPoint.value = null
  originalRatio.value = null
})

function fitImage() {
  if (!canvasRef.value || !imageRef.value) return
  const { clientWidth, clientHeight } = canvasRef.value
  originalRatio.value = simplifyRatio(imageRef.value.naturalWidth, imageRef.value.naturalHeight)
  scale.value = Math.min(1, clientWidth / imageRef.value.naturalWidth, clientHeight / imageRef.value.naturalHeight)
  pan.value = {
    x: (clientWidth - imageRef.value.naturalWidth * scale.value) / 2,
    y: (clientHeight - imageRef.value.naturalHeight * scale.value) / 2,
  }
  if (analysisMode.value === 'auto') void analyzeWhiteArea()
}

function handleModeChange() {
  if (analysisMode.value === 'auto') {
    void analyzeWhiteArea()
  } else {
    clearSelection()
    fitImage()
  }
}

async function analyzeWhiteArea() {
  if (!imageRef.value || !imageRef.value.complete || !imageRef.value.naturalWidth) {
    message.warning('请先上传完成图片后再进行自动解析')
    return
  }

  const source = imageRef.value
  const maxScanSize = 1200
  const scanScale = Math.min(1, maxScanSize / Math.max(source.naturalWidth, source.naturalHeight))
  const width = Math.max(1, Math.round(source.naturalWidth * scanScale))
  const height = Math.max(1, Math.round(source.naturalHeight * scanScale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return

  let pixels: Uint8ClampedArray
  try {
    context.drawImage(source, 0, 0, width, height)
    pixels = context.getImageData(0, 0, width, height).data
  } catch {
    message.error('图片像素读取失败，无法自动解析')
    return
  }

  const total = width * height
  const visited = new Uint8Array(total)
  const queue = new Int32Array(total)
  const minPixels = Math.max(64, Math.floor(total * 0.002))
  const ratios: Array<{ label: AspectRatio; value: number }> = [
    { label: '3:4', value: 3 / 4 },
    { label: '1:1', value: 1 },
    { label: '9:16', value: 9 / 16 },
  ]
  let bestMatch: { x: number; y: number; w: number; h: number; area: number; ratio: AspectRatio; error: number } | null = null

  const isTransparent = (index: number) => {
    const offset = index * 4
    return pixels[offset + 3] <= 5
  }

  for (let index = 0; index < total; index += 1) {
    if (visited[index] || !isTransparent(index)) continue

    let head = 0
    let tail = 0
    let area = 0
    let minX = index % width
    let maxX = minX
    let minY = Math.floor(index / width)
    let maxY = minY
    visited[index] = 1
    queue[tail++] = index

    while (head < tail) {
      const current = queue[head++]
      const x = current % width
      const y = Math.floor(current / width)
      area += 1
      minX = Math.min(minX, x)
      maxX = Math.max(maxX, x)
      minY = Math.min(minY, y)
      maxY = Math.max(maxY, y)

      const above = current - width
      const below = current + width
      if (y > 0 && !visited[above] && isTransparent(above)) { visited[above] = 1; queue[tail++] = above }
      if (y < height - 1 && !visited[below] && isTransparent(below)) { visited[below] = 1; queue[tail++] = below }
      if (x > 0 && !visited[current - 1] && isTransparent(current - 1)) { visited[current - 1] = 1; queue[tail++] = current - 1 }
      if (x < width - 1 && !visited[current + 1] && isTransparent(current + 1)) { visited[current + 1] = 1; queue[tail++] = current + 1 }
    }

    if (area < minPixels) continue
    const componentWidth = maxX - minX + 1
    const componentHeight = maxY - minY + 1
    const componentRatio = componentWidth / componentHeight
    const matchedRatio = ratios.reduce((closest, ratio) => (
      Math.abs(componentRatio - ratio.value) < Math.abs(componentRatio - closest.value) ? ratio : closest
    ))
    const ratioError = Math.abs(componentRatio - matchedRatio.value) / matchedRatio.value
    if (!bestMatch || area > bestMatch.area) {
      bestMatch = {
        x: minX / width,
        y: minY / height,
        w: componentWidth / width,
        h: componentHeight / height,
        area,
        ratio: matchedRatio.label,
        error: ratioError,
      }
    }
  }

  if (!bestMatch) {
    clearSelection()
    message.warning('未找到可识别的连续透明区域')
    return
  }

  selectionStart.value = { x: bestMatch.x, y: bestMatch.y }
  selectionEnd.value = { x: bestMatch.x + bestMatch.w, y: bestMatch.y + bestMatch.h }
  updateSelection(selectionStart.value, selectionEnd.value)
  aspectRatio.value = bestMatch.ratio
  if (bestMatch.error > 0.01) {
    message.warning(`已完成识别；真实边框比与 ${bestMatch.ratio} 的误差为 ${(bestMatch.error * 100).toFixed(2)}%`)
  } else {
    message.success(`已识别 ${bestMatch.ratio} 的透明区域`)
  }
}

function startInteraction(event: MouseEvent) {
  if (analysisMode.value !== 'manual') return
  if (!imageRef.value || !pointInImage(event)) return
  event.preventDefault()
  dragStart.value = { x: event.clientX, y: event.clientY }

  if (event.button === 2) {
    interaction.value = 'panning'
  } else if (event.button === 0) {
    interaction.value = 'selecting'
    const point = pointInImage(event)
    selectionStart.value = point
    selectionEnd.value = point
    updateSelection(point, point)
  }
  window.addEventListener('mousemove', moveInteraction)
  window.addEventListener('mouseup', endInteraction)
}

function moveInteraction(event: MouseEvent) {
  if (!imageRef.value || !canvasRef.value) return

  if (interaction.value === 'panning' && dragStart.value) {
    pan.value = {
      x: pan.value.x + event.clientX - dragStart.value.x,
      y: pan.value.y + event.clientY - dragStart.value.y,
    }
    dragStart.value = { x: event.clientX, y: event.clientY }
    return
  }

  const point = pointInImage(event)
  if (interaction.value === 'selecting' && selectionStart.value) {
    const end = point ?? clampToImage(event)
    selectionEnd.value = end
    updateSelection(selectionStart.value, end)
    return
  }

  hoverPoint.value = point
  if (point) {
    const canvasRect = canvasRef.value.getBoundingClientRect()
    hoverPosition.value = { x: event.clientX - canvasRect.left, y: event.clientY - canvasRect.top }
  }
}

function endInteraction() {
  interaction.value = 'idle'
  dragStart.value = null
  window.removeEventListener('mousemove', moveInteraction)
  window.removeEventListener('mouseup', endInteraction)
}

function clearHover() {
  if (interaction.value === 'idle') hoverPoint.value = null
}

function pointInImage(event: MouseEvent): Point | null {
  if (!imageRef.value) return null
  const rect = imageRef.value.getBoundingClientRect()
  if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return null
  return { x: (event.clientX - rect.left) / rect.width, y: (event.clientY - rect.top) / rect.height }
}

function clampToImage(event: MouseEvent): Point {
  if (!imageRef.value) return { x: 0, y: 0 }
  const rect = imageRef.value.getBoundingClientRect()
  return {
    x: clamp((event.clientX - rect.left) / rect.width),
    y: clamp((event.clientY - rect.top) / rect.height),
  }
}

function updateSelection(start: Point, end: Point) {
  aspectRatio.value = calculateFrameRatio(start, end)
  actualBorderRatio.value = calculateActualFrameRatio(start, end)
  selection.value = {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    w: Math.abs(end.x - start.x),
    h: Math.abs(end.y - start.y),
  }
}

function calculateActualFrameRatio(start: Point, end: Point) {
  if (!imageRef.value) return null
  const width = Math.abs(end.x - start.x) * imageRef.value.naturalWidth
  const height = Math.abs(end.y - start.y) * imageRef.value.naturalHeight
  return width && height ? `${formatCoordinate(width / height)}:1` : null
}

function calculateFrameRatio(start: Point, end: Point) {
  if (!imageRef.value) return null
  const width = Math.round(Math.abs(end.x - start.x) * imageRef.value.naturalWidth)
  const height = Math.round(Math.abs(end.y - start.y) * imageRef.value.naturalHeight)
  return width && height ? simplifyRatio(width, height) : null
}

function clearSelection() {
  selectionStart.value = null
  selectionEnd.value = null
  selection.value = null
  aspectRatio.value = null
  actualBorderRatio.value = null
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    clearSelection()
    endInteraction()
  }
}

function clamp(value: number) {
  return Math.min(1, Math.max(0, value))
}

function formatCoordinate(value: number) {
  const truncatedValue = Math.floor(Math.max(0, value) * 100000)
  const integerPart = Math.floor(truncatedValue / 100000)
  const decimalPart = String(truncatedValue % 100000).padStart(5, '0')
  return `${integerPart}.${decimalPart}`
}

function truncateCoordinate(value: number) {
  return Math.floor(Math.max(0, value) * 100000) / 100000
}

function maximumDisplaySize(start: number) {
  // 预留一个最小精度单位，确保截尾后的起点和尺寸严格小于 1。
  return Math.max(0, truncateCoordinate(1 - start - 0.00001))
}

function simplifyRatio(width: number, height: number) {
  const divisor = greatestCommonDivisor(width, height)
  return `${width / divisor}:${height / divisor}`
}

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b)
}

onMounted(() => window.addEventListener('keydown', handleKeydown))
onBeforeUnmount(() => {
  endInteraction()
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.upload-controls { display: flex; flex-direction: column; gap: 8px; }
.hint { color: #8c8c8c; font-size: 13px; }
.workspace { display: flex; gap: 16px; align-items: flex-start; }
.canvas { position: relative; width: min(760px, calc(100vw - 430px)); height: 560px; overflow: hidden; background: #f5f5f5; border: 1px solid #d9d9d9; user-select: none; }
.canvas :deep(.ant-empty) { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); }
.canvas-image { position: absolute; top: 0; left: 0; display: block; max-width: none; transform-origin: top left; cursor: crosshair; }
.is-panning .canvas-image { cursor: grabbing; }
.selection { position: absolute; pointer-events: none; border: 1px solid #1677ff; background: rgb(22 119 255 / 16%); backdrop-filter: blur(2px); }
.coordinate-tip { position: absolute; z-index: 2; pointer-events: none; padding: 3px 6px; color: #fff; font-size: 12px; white-space: nowrap; background: rgb(0 0 0 / 75%); border-radius: 2px; }
.coordinate-panel { width: 220px; }
@media (max-width: 800px) { .workspace { flex-direction: column; } .canvas { width: 100%; height: 440px; } .coordinate-panel { width: 100%; } }
</style>
